import {
  TableV2,
  PaginationV2,
  ChakraFlex,
  TableColumn,
  useDimensions,
} from '@equip.health/ui';
import { useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { DEFAULT_PAGINATION_CONFIG } from '~/lib/constants';
import { AdminRoles } from '~/lib/constants/permissions.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { Path } from '~/routes/constants';
import UserTableRow from './UserTableRow';

interface UsersTableProps {
  id: string;
  users: GetProviderByIdResponse[];
  isLoading: boolean;
  pageNumber: number;
  totalCount: number;
  handlePageChange: (page: number) => void;
  handleItemPerPageChange: (itemsPerPage: number) => void;
}

const columns: TableColumn<AdminUserRow>[] = [
  { header: 'User', key: 'name' },
  { header: 'Email', key: 'email' },
  { header: 'Cell phone', key: 'phoneNumber' },
  { header: 'Status', key: 'status' },
];

const TABLE_BOTTOM_PADDING = 50;
const PAGINATION_RIGHT_MARGIN = '30px';

const UsersTable = ({
  id,
  users,
  isLoading,
  totalCount,
  pageNumber,
  handlePageChange,
  handleItemPerPageChange,
}: UsersTableProps) => {
  const { roles } = useGlobalContext();
  const history = useHistory();
  const tableRef = useRef<HTMLDivElement>(null);
  const tableYPosition = useDimensions(tableRef, true)?.borderBox.top ?? 0;
  const windowHeight = window?.innerHeight ?? 0;
  const tableHeight =
    windowHeight -
    tableYPosition -
    DEFAULT_PAGINATION_CONFIG.paginationHeight -
    TABLE_BOTTOM_PADDING;
  const rows = useMemo(
    () => users.map((user) => UserTableRow({ id, user })),
    [users, id],
  );

  const hasManageUserRole = useMemo<boolean>(
    () => roles?.includes(AdminRoles.ADMIN_MANAGE_USERS),
    [roles],
  );

  return (
    <ChakraFlex direction="column" gap="20px">
      <ChakraFlex
        direction="column"
        height={tableHeight}
        overflowY="auto"
        ref={tableRef}
      >
        <TableV2
          columns={columns}
          isLoading={isLoading}
          onRowClick={
            hasManageUserRole
              ? (row) => {
                  const userId = row.key;
                  history.push(`${Path.ADMIN_VIEW_PROFILE}/${userId}/info`);
                }
              : undefined
          }
          rows={rows}
        />
      </ChakraFlex>
      <ChakraFlex marginRight={PAGINATION_RIGHT_MARGIN}>
        <PaginationV2
          currentPage={pageNumber}
          itemsPerPageOptions={DEFAULT_PAGINATION_CONFIG.pageSizeOptions}
          onItemsPerPageChange={handleItemPerPageChange}
          onPageChange={handlePageChange}
          tableName="user-table"
          totalItems={totalCount}
        />
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default UsersTable;
